<template>
  <div class="wrapper" :class="{ disabled: !isMounted }">
    <AlertErrorMsg
      ref="alert"
      :title="errorMessage"
      class="page-alert-error"
    />
    <SmallAlert ref="smallAlert" />
    <div class="setting-wrapper">
      <span class="setting-text">Enable Speech-to-Text Transcription</span>
      <div class="toggle-save">
        <div class="toggleWrapper">
          <input
            id="s_stt"
            v-model="configList.speechConfig.value"
            type="checkbox"
            class="dn"
          >
          <label for="s_stt" class="toggle">
            <span class="toggle__handler" />
          </label>
        </div>
      </div>
    </div>
    <div class="collection-setting-wrapper">
      <h2>Set Default Collection Criteria</h2>
      <div class="date-picker-wrapper">
        <p>Please select the time interval and data sources to be collected.</p>
        <date-picker
          :id="`sort_by_date_keys`"
          v-model="dataRange"
          range
          :format="'DD/MM/YYYY'"
          :range-separator="' - '"
          :editable="false"
          class="datepicker-input"
          :input-class="'datepicker-input'"
          placeholder="Select Date range"
          :disabled-date="disableFutureDates"
          :style="{ width: formattedDateRange ? '450px' : '250px' }"
        >
          <template v-if="formattedDateRange" #input>
            <p class="date-chosen">
              <b>Currently selected time interval: </b>{{ formattedDateRange }}
            </p>
          </template>
          <template #icon-calendar>
            <CalendarIcon25 />
          </template>
          <template #icon-clear>
            <BlueCloseIcon25 />
          </template>
        </date-picker>
        <div
          class="error"
          :class="{ visible:
            !formattedDateRange
            && (!dataRange
              && !$v.dataRange.required)
          }"
        >
          Field is required
        </div>
      </div>
      <p style="margin-top: 22px;">
        Data sources *
      </p>
      <div class="sources-wrapper">
        <CustomCheckbox
          id="sms"
          :label="'SMS'"
          :input-element="configList.appConfig.value.sms"
        />
        <CustomCheckbox
          id="whatsApp"
          :label="'WhatsApp'"
          :input-element="configList.appConfig.value.whatsapp"
        />
        <CustomCheckbox
          id="wechat"
          :label="'WeChat'"
          :input-element="configList.appConfig.value.wechat"
        />
        <CustomCheckbox
          id="viber"
          :label="'Viber'"
          :input-element="configList.appConfig.value.viber"
        />
        <CustomCheckbox
          id="gallery"
          :label="'Gallery'"
          :input-element="configList.galleryConfig"
        />
        <CustomCheckbox
          id="apps"
          :label="'AppList'"
          :input-element="configList.appConfig.value.appList"
        />
        <CustomCheckbox
          id="iMessage"
          :label="'iMessage'"
          :input-element="configList.appConfig.value.iMessage"
        />
        <div
          class="contacts-toggle-wrapper"
          @mouseenter="showCallLogsTooltip = true"
          @mouseleave="showCallLogsTooltip = false"
        >
          <CustomCheckbox
            id="call_logs"
            :label="'Call Logs'"
            :input-element="configList.appConfig.value.callLogs"
          />
          <transition name="fade">
            <span v-if="showCallLogsTooltip" class="tooltip">Call logs are collected not by time interval</span>
          </transition>
        </div>
        <div
          class="contacts-toggle-wrapper"
          @mouseenter="showContactsTooltip = true"
          @mouseleave="showContactsTooltip = false"
        >
          <CustomCheckbox
            id="contacts_toggle"
            :label="'Contacts'"
            :input-element="configList.appConfig.value.contacts"
          />
          <transition name="fade">
            <span v-if="showContactsTooltip" class="tooltip">Contacts are collected not by time interval</span>
          </transition>
        </div>
      </div>
      <div v-show="configList.galleryConfig.isActive" class="gallery-settings-wrapper">
        <h3>Gallery configuration</h3>
        <div class="file-type-select">
          <p>Select which file type to be uploaded</p>
          <div class="select-wrapper">
            <CustomCheckbox
              id="videos"
              label="Videos"
              :input-element="configList.galleryConfig.value.video"
            />
            <CustomCheckbox
              id="photos"
              label="Photos"
              :input-element="configList.galleryConfig.value.photo"
            />
          </div>
        </div>
        <div class="file-size-select">
          <p>Choose the size range for the files you want to upload:</p>
          <div class="size-select-wrapper">
            <div class="toggleWrapper">
              <input
                id="uploadCompact"
                v-model="configList.galleryConfig.value.size"
                type="radio"
                name="uploadCompact"
                value="compact"
              >
              <label for="email-2fa" class="toggle">
                <div class="radio-label-wrapper">
                  <span class="toggle__handler">Compact</span>
                </div>
              </label>
            </div>
            <p>Upload only files up to 10Mb.</p>
            <div class="toggleWrapper">
              <input
                id="uploadAll"
                v-model="configList.galleryConfig.value.size"
                type="radio"
                name="uploadAll"
                value="all"
              >
              <label for="sms-2fa" class="toggle">
                <div class="radio-label-wrapper">
                  <span class="toggle__handler">Everything</span>
                </div>
              </label>
            </div>
            <p>Upload all files</p>
          </div>
        </div>
      </div>
      <div v-if="lastUpdatedBy" class="updated-by">
        &ast;Last update made by
        <span style="cursor: pointer" @click="gotoProfile(lastUpdatedBy.publicId)">
          {{
            lastUpdatedBy.firstName + " " + lastUpdatedBy.lastName
              + " on " + parseDate(lastUpdatedBy.time)
              + " at " + parseDateTime(lastUpdatedBy.time)
          }}
        </span>
      </div>
    </div>
    <CommonDialog ref="commonDialogFormSuccess">
      <SuccessIcon />
      <h2 class="dialog-message-title">
        Success!
      </h2>
      <p class="dialog-message-description" style="display:block;">
        Case settings were changed successfully
      </p>
    </CommonDialog>
  </div>
</template>

<script>
import CommonDialog from '@/components/popups/CommonDialog.vue';
import DatePicker from 'vue2-datepicker';
import CustomCheckbox from '@/components/inputs/CustomCheckbox.vue';
import AlertErrorMsg from '@/components/profile/AlertErrorMsg.vue';
import {required} from 'vuelidate/lib/validators';
import SmallAlert from '@/components/alerts/SmallAlert.vue';
import parseDate from '@/mixins/parseDate';
import parseDateTime from '@/mixins/parseTime';
import {CASE_CONFIG_ENUM} from '@/constants/const';
import errorHandler from '@/mixins/errorHandler';
import CalendarIcon25 from '@/components/icons/CalendarIcon25.vue';
import BlueCloseIcon25 from '@/components/icons/BlueCloseIcon25.vue';
import SuccessIcon from '@/components/icons/SuccessIcon.vue';

export default {
  name: 'CaseSettingsGeneral',
  components: {
    SuccessIcon,
    BlueCloseIcon25,
    CalendarIcon25,
    SmallAlert,
    AlertErrorMsg,
    CustomCheckbox,
    CommonDialog,
    DatePicker,
  },
  mixins: [parseDate, parseDateTime, errorHandler],
  data() {
    return {
      selectedDate: [],
      errorMessage: '',
      lastUpdatedBy: null,
      dataRange: null,
      isMounted: false,
      isDatePickerEnabled: false,
      showContactsTooltip: false,
      showCallLogsTooltip: false,
      initialConfig: null,
      configList: {
        speechConfig: {
          configName: CASE_CONFIG_ENUM.speech,
          value: false,
        },
        dateFromConfig: {
          configName: CASE_CONFIG_ENUM.dateFrom,
          value: null,
        },
        dateToConfig: {
          configName: CASE_CONFIG_ENUM.dateTo,
          value: null,
        },
        appConfig: {
          configName: CASE_CONFIG_ENUM.appList,
          value: {
            sms: {isActive: false},
            wechat: {isActive: false},
            whatsapp: {isActive: false},
            viber: {isActive: false},
            appList: {isActive: false},
            iMessage: {isActive: false},
            contacts: {isActive: false},
            callLogs: {isActive: false},
          },
        },
        galleryConfig: {
          configName: CASE_CONFIG_ENUM.gallery,
          value: {
            video: {isActive: false},
            photo: {isActive: false},
            size: '',
          },
          isActive: false,
        }
      },
    };
  },
  validations: {
    dataRange: {
      required,
    },
  },
  computed: {
    formattedDateRange() {
      if (this.selectedDate.length > 0) {
        const adjustedDateFrom = !isNaN(this.selectedDate[0]) ? new Date(this.selectedDate[0]) : null;
        const adjustedDateTo = !isNaN(this.selectedDate[1]) ? new Date(this.selectedDate[1]) : null;

        return `${this.parseDate(adjustedDateFrom)} - ${this.parseDate(adjustedDateTo)}`;
      }
      return null;
    },
  },
  watch: {
    dataRange(value) {
      if (value) {
        this.configList.dateFromConfig.value = isNaN(Date.parse(value[0])) == false ? Date.parse(value[0]) : '';
        this.configList.dateToConfig.value = isNaN(Date.parse(value[1])) == false ? Date.parse(value[1]) : '';
        this.selectedDate = [value[0], value[1]];
        this.saveCaseConfig();
      }
    },
    'configList.speechConfig.value'() {
      this.saveCaseConfig();
    },
    'configList.appConfig.value': {
      handler() {
        this.saveCaseConfig();
      },
      deep: true,
    },
    'configList.galleryConfig.value': {
      handler() {
        this.saveCaseConfig();
      },
      deep: true,
    },
    'configList.galleryConfig.isActive': {
      handler(newValue) {
        if (!newValue) {
          this.configList.galleryConfig.value = {
            video: {isActive: false},
            photo: {isActive: false},
          };
          this.saveCaseConfig();
        }
      },
      deep: true,
    },
  },
  created() {
    this.getCaseConfig();
  },
  mounted() {
    setTimeout(() => this.isMounted = true, 1000);
  },
  methods: {
    disableFutureDates(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date > today;
    },
    gotoProfile(id) {
      let route = this.$router.resolve({name: 'User Profile', params: {slug: id}});
      window.open(route.href, '_blank');
    },
    async getCaseConfig() {
      this.$load(async () => {
        const res = await this.$api.caseConfig.getCaseConfig(this.$route.params.slug);

        if (res.data.body.configs.length > 0) {
          res.data.body.configs.forEach((item) => {
            Object.values(this.configList).forEach((cfg) => {
              if (cfg.configName === item.configName) {
                if (cfg.configName === CASE_CONFIG_ENUM.appList) {
                  if (item.value) {
                    cfg.value = this.stringToAppConfig(item.value);
                  }
                } else if (cfg.configName === CASE_CONFIG_ENUM.gallery) {
                  cfg.value = this.stringToGalleryConfig(item.value);
                  if (item.value.includes('photo') || item.value.includes('video')) {
                    cfg.isActive = true;
                  }
                } else if (item.value === 'false') {
                  cfg.value = false;
                } else if (item.value === 'true') {
                  cfg.value = true;
                } else {
                  cfg.value = item.value;
                }
              }
            });
          });
          this.initialConfig = JSON.parse(JSON.stringify(this.configList));
        }
        this.getDateRange();

        if (res.data.body.updatedBy) {
          this.lastUpdatedBy = res.data.body.updatedBy;
          this.lastUpdatedBy.time = res.data.body.lastUpdateAt;
        }

      }, (error => {
        this.handleResponseError(error);
      }));
    },
    configObjectToArray() {
      const configObject = [];
      const isConfigChanged = (key, config) => this.initialConfig[key].value !== config.value;

      Object.entries(this.configList).forEach(([key, config]) => {
        if (!isConfigChanged(key, config)) return;

        let valueAsString;
        if (config.configName === CASE_CONFIG_ENUM.appList) {
          if (!this.hasAppListChanged(key, config)) return;
          valueAsString = this.getActiveApps(config);
        } else if (config.configName === CASE_CONFIG_ENUM.gallery) {
          if (!this.hasGalleryConfigChanged(key, config)) return;
          valueAsString = this.getGalleryConfig(config);
        } else {
          valueAsString = config.value !== null && config.value !== undefined
            ? String(config.value)
            : '';
        }

        this.initialConfig[key].value = JSON.parse(JSON.stringify(config.value));

        configObject.push({
          configName: config.configName,
          value: valueAsString,
        });
      });

      return configObject;
    },
    hasAppListChanged(key, config) {
      return Object.entries(this.initialConfig[key].value).some(([key, value]) => {
        return config.value[key].isActive !== value.isActive;
      });
    },
    getActiveApps(config) {
      return Object.keys(config.value)
        .filter(service => config.value[service].isActive)
        .join(',') || '';
    },
    hasGalleryConfigChanged(key, config) {
      return JSON.stringify(this.initialConfig[key].value) !== JSON.stringify(config.value);
    },
    getGalleryConfig(config) {
      const result = [];

      Object.entries(config.value).forEach(([key, value]) => {
        if (typeof value === 'object' && value.isActive === true) {
          result.push(key);
        } else if (typeof value === 'boolean' && value === true) {
          result.push(key);
        } else if (typeof value === 'string' && value.trim() !== '' && value !== 'all') {
          result.push(value);
        }
      });

      return result.join(',');
    },
    stringToAppConfig(activeServicesString) {
      const allServices = ['sms', 'whatsapp', 'wechat', 'viber', 'appList', 'contacts', 'callLogs', 'iMessage'];
      const activeServices = activeServicesString.split(',');

      const appConfig = {};
      allServices.forEach(service => {
        appConfig[service] = {isActive: activeServices.includes(service)};
      });

      return appConfig;
    },
    stringToGalleryConfig(galleryConfigString) {
      const values = galleryConfigString.split(',').map(item => item.trim());

      return {
        video: {isActive: values.includes('video')},
        photo: {isActive: values.includes('photo')},
        size: values.includes('compact') ? 'compact' : 'all',
      };
    },
    saveCaseConfig() {
      this.$v.$touch();
      if (this.isMounted && this.formattedDateRange) {
        this.$load(async () => {
          await this.$api.caseConfig.updateCaseConfig(this.$route.params.slug, {
            publicIds: [],
            caseConfigurationList: this.configObjectToArray(),
          });
          this.$refs.smallAlert.showAlert();
        });
      }
    },
    openDialogFormSuccess() {
      this.$refs.commonDialogFormSuccess.openPopups();
    },
    getDateRange() {
      const dateFrom = Number(this.configList.dateFromConfig.value);
      const dateTo = Number(this.configList.dateToConfig.value);

      if (dateFrom) {
        this.selectedDate = [dateFrom, dateTo];
      }
    }
  }
};
</script>

<style scoped lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;

  .setting-wrapper {
    display: flex;
    align-items: center;
    margin: 0 50px 10px 0;
    height: 35px;

    .setting-text {
      display: block;
      width: 500px;
      font-size: 14px;
      font-weight: 500;
      line-height: 21px;
      text-align: left;
      color: var(--secondary-black-color);
      margin-right: 50px;
    }

    .toggle-save {
      display: flex;
      width: 30%;
      justify-content: space-between;

      .toggleWrapper {
        input {
          position: absolute;
          left: -99em;
        }
      }

      .toggle {
        cursor: pointer;
        display: inline-block;
        position: relative;
        width: 40px;
        height: 20px;
        border-radius: 90px;
        background-color: var(--white-color);
        border: 1px solid var(--light-gray);
        transition: all 400ms;
      }

      .toggle__handler {
        display: inline-block;
        position: relative;
        z-index: 1;
        top: 1px;
        left: 3.5px;
        width: 13px;
        height: 13px;
        border-radius: 50px;
        transition: all 400ms;
        background-color: var(--input-placeholder-value-color);
        transform: rotate(-50deg);
      }

      input:checked {
        + .toggle {
          background-color: var(--accent-color);
          border: 1px solid var(--accent-color);

          .toggle__handler {
            background-color: var(--white-color);
            transform: translate3d(18px, 0, 0) rotate(0);
          }
        }
      }
    }
  }

  .collection-setting-wrapper {

    .contacts-settings {
      display: flex;
      flex-direction: column;
      gap: 32px;
      margin-top: 32px;

      .setting-wrapper {
        flex-direction: row;
        margin: 0;
      }
    }

    .setting-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
      margin-bottom: 55px;

      .change-date-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        max-width: 790px;
      }
    }

    h2 {
      font-size: 24px;
      font-weight: 700;
      line-height: 36px;
      text-align: left;
      color: var(--secondary-black-color);
    }

    p {
      font-size: 14px;
      font-weight: 500;
      line-height: 21px;
      text-align: left;
      color: var(--secondary-black-color);
    }

    .date-picker-wrapper {
      display: flex;
      flex-direction: column;
      gap: 5px;

      .datepicker-input {
        width: 100%;
        max-width: 450px;
      }

      .date-chosen {
        width: 100%;
        height: 35px;
        font-size: 14px;
        font-weight: 500;
        line-height: 33px;
        text-align: left;
        margin: 0;
      }
    }

    .sources-wrapper {
      max-width: 265px;
      width: 100%;
      height: 210px;
      display: flex;
      flex-direction: column;
      gap: 15px;
      flex-wrap: wrap;
      margin-bottom: 15px;

      & > * {
        max-width: 100px;
        width: 100%;
      }

      .contacts-toggle-wrapper {
        position: relative;

        .tooltip {
          all: unset;
          display: block;
          position: absolute;
          top: 0;
          left: 0;
          pointer-events: none;
          background-color: rgba(0, 0, 0, 0.75);
          color: #fff;
          z-index: 100;
          padding: 5px 10px;
          border-radius: 4px;
          font-size: 12px;
          transform: translate(40%, -15%);
          white-space: nowrap;

          &:before {
            top: 50%;
            left: -6px;
            transform: rotate(90deg);
          }
        }
      }
    }

    .visible {
      display: block;
    }

    .gallery-settings-wrapper {
      background-color: var(--active-accent-color);
      max-width: 615px;
      width: 100%;
      max-height: 335px;
      height: 335px;
      padding: 24px;
      display: flex;
      flex-direction: column;
      gap: 24px;

      h3 {
        margin: 0;
        font-weight: 700;
        font-size: 14px;
        line-height: 150%;
        vertical-align: middle;
        text-transform: uppercase;
        color: var(--secondary-black-color);
      }

      .file-type-select {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding-bottom: 10px;
        max-width: 266px;
        width: 100%;

        p {
          margin: 0;
          font-weight: 500;
          font-size: 14px;
          line-height: 150%;
          vertical-align: middle;
          color: var(--secondary-black-color);
        }

        .select-wrapper {
          display: flex;
          align-items: center;
          gap: 140px;
        }
      }

      .file-size-select {
        display: flex;
        flex-direction: column;
        gap: 16px;
        max-width: 365px;
        width: 100%;

        p {
          margin: 0;
          font-weight: 500;
          font-size: 14px;
          line-height: 150%;
          vertical-align: middle;
          color: var(--secondary-black-color);
        }

        .size-select-wrapper {
          display: flex;
          flex-direction: column;
          gap: 8px;

          .toggleWrapper {
            display: flex;
            align-items: center;
            gap: 10px;

            .toggle {
              margin-bottom: 0;

              span {
                font-weight: 600;
                font-size: 14px;
                line-height: 100%;
                vertical-align: middle;
                color: var(--secondary-black-color);
              }
            }

            input {
              height: fit-content;
              margin: 2px 0 0;
            }

            input[type="radio"] {
              appearance: none;
              -webkit-appearance: none;
              width: 20px;
              height: 20px;
              border: 4px solid #EBEFF6;
              border-radius: 50%;
              position: relative;
              cursor: pointer;
              background-color: var(--white-color);
            }

            input[type="radio"]:checked::before {
              content: '';
              position: absolute;
              width: 100%;
              height: 100%;
              background-color: var(--accent-color);
              border-radius: 50%;
            }
          }

          p {
            font-weight: 400;
            font-size: 14px;
            line-height: 100%;
            vertical-align: middle;
            color: var(--secondary-black-color);
            margin: 0 0 10px 30px;
          }
        }
      }
    }

    .updated-by {
      font-size: 14px;
      font-weight: 500;
      line-height: 21px;
      text-align: left;
      color: var(--light-gray);
      margin-top: 32px;

      span {
        color: var(--accent-color);
      }
    }

  }

  .btn-save-settings {
    width: 400px;
    height: 50px;
    margin: 30px 30px 0 0;
    padding: 14px 20px;
    border-radius: 25px;
  }
}

.disabled {
  pointer-events: none;
  opacity: 0.4;
  transition: all .5s;
}
</style>
